<script lang="ts" setup>
import axios from 'axios'
import { api as viewerApi } from 'v-viewer'
import { onBeforeMount, ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { notify } from '@kyvg/vue3-notification'

import { datetime, differenceBetweenDates, differenceFromMinutes } from '@/utils/dates'
import { image as imageHelper, localAsset } from '@/utils/assets'
import { Marker, ResourceResponse, uuid } from '@/types/general'
import { LocationPoint, LicenseType } from '@/types/company'
import {
    Report,
    EventType,
    ReportEvent,
    EventImageType,
    EventActionType,
    tourTypeTranslations,
} from '@/types/driver-report'
import { useAuthStore } from '@/stores/auth-store'
import { printElement } from '@/utils/print-element'
import { googleMapsLink } from '@/utils/google-maps'
import { smallDotSvg } from '@/utils/maps'
import { useMittListener } from '@/hooks/use-mitt-listener'
import { useDeleteDriverReport } from '@/hooks/driver-reports/use-delete-driver-report'
import { canSettleReport, reportColors } from '@/utils/driver-report'
import { useConfirm } from '@/hooks/use-confirm'
import { MyButtonScheme } from '@/types/layout/my-button'
import { emitter } from '@/utils/mitt'

import MyMenu from '@/components/my-components/MyMenu.vue'
import MyMenuItem from '@/components/my-components/MyMenuItem.vue'
import MapComponent from '@/components/MapComponent.vue'
import StopReportModal from '@/components/driver-reports/StopReportModal.vue'
import ManageEventModal from '@/components/driver-reports/ManageEventModal.vue'
import ContentHeading from '@/components/layout/ContentHeading.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyModal from '@/components/my-components/MyModal.vue'
import MyPanel from '@/components/my-components/MyPanel.vue'
import ActionRowItem from '@/components/table/ActionRowItem.vue'
import MyTable from '@/components/table/MyTable.vue'
import MyTableColumn from '@/components/table/MyTableColumn.vue'
import ShareReportModal from '@/components/driver-reports/ShareReportModal.vue'
import UpdateReportTypeModal from '@/components/driver-reports/UpdateReportTypeModal.vue'

export interface Props {
    id?: string
    open?: boolean
}

const emit = defineEmits<{ (e: 'update:open', value: boolean): void }>()
const props = withDefaults(defineProps<Props>(), { open: true })

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const authStore = useAuthStore()
const deleteDriverReport = useDeleteDriverReport()
const confirm = useConfirm()

const modalContent = ref<HTMLDivElement>()
const createEventModalOpen = ref(false)
const updateEventModalOpen = ref(false)
const stopReportModalOpen = ref(false)
const selectedEventId = ref<uuid>()
const mapLocation = ref<LocationPoint>()

const report = ref<Report | null>(null)
const loading = ref(false)
const shareReportOpen = ref(false)
const imageViewerOpen = ref(false)
const updateReportTypeModalOpen = ref(false)
const reportTrackingEvents = ref<LocationPoint[]>([])

const markers = computed<Marker[]>(() => {
    const markerList: Marker[] = []

    if (!report.value || !report.value.events) return markerList

    if (reportTrackingEvents.value.length > 0) {
        const trackingPoints = reportTrackingEvents.value.filter(
            (point) => point.latitude && point.longitude,
        )
        trackingPoints.forEach((trackingEvent, index) => {
            markerList.push({
                location: trackingEvent,
                markerId: `tracking-${report.value!.id}-${index}`,
                customSvg: smallDotSvg,
            })
        })
    }

    // Show report events with numbered labels (alongside tracking events)
    report.value.events.forEach((event, index) => {
        if (event.location) {
            const tooltipContent =
                event.address ?? `${event.location.latitude}, ${event.location.longitude}`

            markerList.push({
                label: index + 1,
                location: event.location,
                content: tooltipContent,
                href: googleMapsLink(event.location.latitude, event.location.longitude),
            })
        }
    })
    return markerList
})

const eventActionsExist = computed(() => {
    return report.value?.events.some((event) => {
        if (isFerryEvent(event)) return false
        if (event.tourEvents.length > 0) return true
        if (event.tourId) return true

        return event.actions.some((action) => action.type !== EventActionType.AttachUnit)
    })
})

const shadowReportsMap = computed(() => {
    return new Map(
        report.value?.shadowReports?.map((report, index) => [
            report.id,
            {
                report,
                color: reportColors[index],
                title: `${report.referenceNumber ?? t('noReferenceNumber')} - ${report.company.name}`,
            },
        ]),
    )
})

const hasShadowReports = computed(() => {
    return report.value?.shadowReports && report.value.shadowReports.length > 0
})

const isNotReadonly = computed(() => !authStore.isReadonly.driverReport)

async function fetchReport() {
    loading.value = true
    try {
        const response = await axios.get<ResourceResponse<Report>>(
            window.route('dr.reports.show', {
                report: props.id ?? route.params.reportId,
            }),
        )
        report.value = response.data.data
        loading.value = false
        await fetchReportTrackingEvents()
    } finally {
        loading.value = false
    }
}

async function fetchReportTrackingEvents() {
    if (!report.value?.id) return
    if (!authStore.hasLicense(LicenseType.LocationTracking)) return

    reportTrackingEvents.value = []
    const { data } = await axios.get<ResourceResponse<LocationPoint[]>>(
        window.route('tracking-events.for-report', {
            report: report.value.id,
            includeEndedSessions: true,
        }),
    )
    reportTrackingEvents.value = data.data
}

function panToLocation(location: LocationPoint) {
    mapLocation.value = location
}

// Endpoint Related
function selectEvent(id: string) {
    selectedEventId.value = id
}

function updateEvent(eventId: string) {
    selectEvent(eventId)
    updateEventModalOpen.value = true
}

const selectedEvent = computed(() => {
    if (!selectedEventId.value) return undefined

    return report.value!.events.find(({ id }) => id === selectedEventId.value)
})

const startNote = computed(() => {
    return report.value?.events.find((event) => event.type === EventType.ReportStart)?.departureNote
})

const endNote = computed(() => {
    return report.value?.events.find((event) => event.type === EventType.ReportEnd)?.arrivalNote
})

const hasOngoingEvent = computed(() => {
    if (report.value?.events.find((event) => event.endedAt === null)) return true
    return false
})

const eventTypes = computed<Record<EventType, string>>(() => {
    return {
        [EventType.ReportStart]: t('start'),
        [EventType.ReportEnd]: t('finish'),
        [EventType.Break]: t('break'),
        [EventType.Customer]: t('customer'),
        [EventType.Traffic]: t('traffic'),
        [EventType.Other]: t('other'),
        [EventType.Resting]: t('resting'),
        [EventType.StartTour]: t('tourStarted'),
        [EventType.ContractorReportStart]: hasShadowReports.value
            ? t('contractorReportStarted')
            : t('start'),
        [EventType.ContractorReportStop]: hasShadowReports.value
            ? t('contractorReportStopped')
            : t('finish'),
    }
})

const totalKilometerCount = computed(() => {
    return report.value!.events.at(-1)!.kilometerCount - report.value!.events[0].kilometerCount
})

const totalBreakTime = computed(() => {
    const breakEvents = report
        .value!.events.filter((event) => event.type === EventType.Break)
        .map((event) => {
            const diff = new Date(event.endedAt!).getTime() - new Date(event.startedAt).getTime()
            return Math.floor(diff / 1000 / 60)
        })

    return differenceFromMinutes(breakEvents.reduce((a, b) => a + b, 0))
})

const isNoReadonly = computed(() => !authStore.isReadonly.driverReport)

// We can assume ferry, because there is no other traffic stops with a linked tour event.
// This will need to be changed if we add other types of traffic, with a linked tour event.
function isFerryEvent(event: ReportEvent): boolean {
    return event.tourEvents.length > 0 && event.type === EventType.Traffic
}

function eventTypeText(reportEvent: ReportEvent): string {
    if (isFerryEvent(reportEvent)) return t('ferry')

    return t(eventTypes.value[reportEvent.type])
}

function showArrivalImages(event: ReportEvent) {
    const arrivalImages = event.images.filter(
        (image) => image.type === EventImageType.Arrival && image.path,
    )

    if (arrivalImages.length > 0) {
        viewerApi({
            images: arrivalImages.map((image) => imageHelper(image.path!, 'public')),
            options: {
                url: 'src',
                title: false,
            },
        })
    }
}

function showDepartureImages(event: ReportEvent) {
    const departureImages = event.images.filter(
        (image) => image.type === EventImageType.Departure && image.path,
    )

    if (departureImages.length > 0) {
        viewerApi({
            images: departureImages.map((image) => imageHelper(image.path!, 'public')),
            options: {
                url: 'src',
                title: false,
            },
        })
    }
}

async function settleReport(id: uuid) {
    try {
        await confirm(t('settleReport'), t('settleReportDescription'), {
            confirmText: t('yes'),
            cancelText: t('no'),
            confirmButtonScheme: MyButtonScheme.Primary,
        })

        const response = await axios.put<uuid[]>(
            window.route('dr.company.reports.settle', {
                company: authStore.companyId,
            }),
            { reportIds: [id] },
        )

        if (response) {
            notify({ type: 'success', text: t('reportSettled') })
            fetchReport()
            emitter.emit('fetchDriverReports')
        }
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 409) {
            notify({ type: 'error', text: t('noSalaryNumber') })
        }
    }
}

async function createEventModalClosed() {
    createEventModalOpen.value = false
    fetchReport()
}

async function updateEventModalClosed() {
    updateEventModalOpen.value = false
    fetchReport()
}

async function stopReportModalClosed() {
    stopReportModalOpen.value = false
}

function print() {
    printElement(modalContent.value!, true)
}

function showImage(path: string) {
    viewerApi({
        images: [imageHelper(path, 'public')],
        options: {
            url: 'src',
            toolbar: false,
            navbar: false,
            title: false,
        },
    })
}

function onClose() {
    if (imageViewerOpen.value) return
    if (props.id) {
        emit('update:open', false)
    } else {
        router.replace({ name: 'dr.reports', query: route.query })
    }
}

function getEventRegistrationNumbers(event: ReportEvent): string {
    return event.actions
        .filter((action) => action.type === EventActionType.AttachUnit)
        .map((action) => report.value?.unitRegistrationNumbers[action.actionableId] ?? '')
        .join(', ')
}

async function deleteReport(report: Report) {
    if (await deleteDriverReport(report)) {
        onClose()
    }
}

useMittListener('fetchDriverReports', () => fetchReport())

onBeforeMount(async () => {
    if (route.params.reportId) fetchReport()
    if (props.id) await fetchReport()
})

watch(() => props.id, fetchReport)
watch(
    () => route.params.reportId,
    () => {
        if (route.params.reportId) fetchReport()
    },
)
</script>

<template>
    <MyModal :value="props.open" :max-width="1400" @close="onClose">
        <LoaderWrapper :visible="loading" />
        <div v-if="report" ref="modalContent">
            <div class="mb-6 flex justify-between">
                <ContentHeading>
                    <h1 class="text-xl font-bold text-primary-400">{{ t('driverReport') }}</h1>
                    <div class="mt-1 flex space-x-2 text-xs">
                        <h4 class="font-semibold text-primary-500 dark:text-primary-300">
                            {{ t('referenceNumber') }}:
                        </h4>
                        <h4 class="font-medium dark:text-primary-400">
                            {{ report.referenceNumber ?? t('notSpecified') }}
                        </h4>
                    </div>
                </ContentHeading>

                <div
                    v-if="authStore.companyId && report"
                    class="flex space-x-2 print:hidden items-center"
                >
                    <MyButton
                        v-if="report.endedAt === null && isNoReadonly && !report.originalReportId"
                        scheme="danger"
                        size="small"
                        @click="stopReportModalOpen = true"
                    >
                        {{ t('stopReport') }}
                    </MyButton>

                    <MyButton
                        v-if="canSettleReport(report, authStore.companyId) && isNotReadonly"
                        v-tooltip="t('settleReportDescription')"
                        scheme="primary"
                        size="small"
                        :loading="loading"
                        @click="settleReport(report.id)"
                    >
                        {{ t('settleReport') }}
                        <mdi:file-document-check-outline class="ml-2" />
                    </MyButton>

                    <MyButton icon plain scheme="light" size="small" @click="print()">
                        <mdi:printer />
                    </MyButton>

                    <MyButton
                        v-if="!!authStore.user"
                        v-tooltip="t('shareEntity', { entity: t('report') })"
                        icon
                        plain
                        scheme="light"
                        size="small"
                        @click="shareReportOpen = true"
                    >
                        <mdi:share />
                    </MyButton>

                    <MyMenu
                        v-if="!report.originalReportId"
                        hide-arrow
                        class="relative z-20 !h-auto"
                        width="w-60"
                    >
                        <template #trigger>
                            <MyButton icon plain scheme="light" size="small">
                                <mdi:dots-vertical />
                            </MyButton>
                        </template>

                        <div
                            v-tooltip="{
                                disabled: !hasOngoingEvent,
                                content: t('hasOngoingEventExplanation'),
                            }"
                        >
                            <MyMenuItem
                                :disabled="hasOngoingEvent"
                                @click="createEventModalOpen = true"
                            >
                                <mdi:plus class="mr-2" />
                                <span v-text="t('createEvent')" />
                            </MyMenuItem>
                        </div>

                        <MyMenuItem @click="updateReportTypeModalOpen = true">
                            <mdi:update class="mr-2" />
                            <span v-text="t('changeReportType')" />
                        </MyMenuItem>

                        <MyMenuItem @click="deleteReport(report)">
                            <mdi:trash-can class="mr-2" />
                            <span v-text="t('deleteEntityTitle', { entity: t('report') })" />
                        </MyMenuItem>
                    </MyMenu>
                </div>
            </div>

            <section class="mb-10 flex w-full items-start rounded-lg bg-white dark:bg-dark-500">
                <div class="m-6 grid w-1/2 grid-cols-3 gap-3">
                    <div class="col-span-3 flex flex-col">
                        <span
                            class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                            v-text="t('driver')"
                        />
                        <span class="text-xl font-semibold text-primary-500 dark:text-primary-100">
                            {{ report.user.name }}
                        </span>

                        <span v-if="report.haulierCompany" class="text-xs">
                            {{ report.company.name }}
                        </span>
                    </div>

                    <div class="flex flex-col">
                        <span
                            class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                            v-text="t('kmTotal')"
                        />
                        <span class="text-xl font-semibold text-primary-500 dark:text-primary-100">
                            {{ totalKilometerCount }} KM
                        </span>
                    </div>

                    <div class="flex flex-col">
                        <span
                            class="text-xs font-semibold text-primary-500 dark:text-primary-300"
                            v-text="t('vehicle')"
                        />
                        <span class="text-sm text-primary-500 dark:text-primary-100">
                            {{ report.vehicle?.registrationNumber ?? t('notSpecified') }}

                            <RouterLink
                                v-if="report.damageReportPickupId"
                                :to="{
                                    name: 'dmr.reports.show',
                                    params: { reportId: report.damageReportPickupId },
                                }"
                                class="block font-semibold uppercase"
                                v-text="`${t('pickup')} ${t('report')}`"
                            />

                            <RouterLink
                                v-if="report.damageReportDropOffId"
                                :to="{
                                    name: 'dmr.reports.show',
                                    params: { reportId: report.damageReportDropOffId },
                                }"
                                class="block font-semibold uppercase"
                                v-text="`${t('dropOff')} ${t('report')}`"
                            />
                        </span>
                    </div>

                    <div class="flex flex-col">
                        <span
                            class="text-xs font-semibold text-primary-500 dark:text-primary-300"
                            v-text="t('haulier')"
                        />
                        <span class="text-sm text-primary-500 dark:text-primary-100">
                            {{ report?.haulierCompany?.name ?? t('notSpecified') }}
                        </span>
                    </div>

                    <div class="flex flex-col">
                        <span
                            class="text-xs font-semibold text-primary-500 dark:text-primary-300"
                            v-text="t('start')"
                        />
                        <span class="text-sm text-primary-500 dark:text-primary-100">
                            {{ datetime(report.startedAt) }}
                        </span>
                    </div>

                    <div class="flex flex-col">
                        <span
                            class="text-xs font-semibold text-primary-500 dark:text-primary-300"
                            v-text="t('end')"
                        />
                        <span class="text-sm text-primary-500 dark:text-primary-100">
                            {{ report?.endedAt ? datetime(report.endedAt) : t('enRoute') }}
                        </span>
                    </div>

                    <div class="flex flex-col">
                        <span
                            class="text-xs font-semibold text-primary-500 dark:text-primary-300"
                            v-text="t('type')"
                        />
                        <span class="text-sm text-primary-500 dark:text-primary-100">
                            {{ t(tourTypeTranslations[report.tourType]) }}
                        </span>
                    </div>

                    <div>
                        <div class="flex items-center">
                            <span
                                class="text-xs font-semibold text-primary-500 dark:text-primary-300"
                                v-text="t('duration')"
                            />
                        </div>
                        <span
                            v-tooltip="t('hourMinuteFormatExplanation')"
                            class="text-sm text-primary-500 dark:text-primary-100"
                            v-text="
                                differenceBetweenDates(
                                    report.startedAt,
                                    report.endedAt,
                                    report.restingTime,
                                )
                            "
                        />
                    </div>

                    <div>
                        <div class="flex items-center">
                            <span
                                class="text-xs font-semibold text-primary-500 dark:text-primary-300"
                                v-text="t('restingTime')"
                            />
                        </div>
                        <span
                            v-tooltip="t('hourMinuteFormatExplanation')"
                            class="text-sm text-primary-500 dark:text-primary-100"
                            v-text="differenceFromMinutes(report.restingTime)"
                        />
                    </div>

                    <div>
                        <div class="flex items-center">
                            <span
                                class="text-xs font-semibold text-primary-500 dark:text-primary-300"
                                v-text="t('breakTime')"
                            />
                        </div>
                        <span
                            v-tooltip="t('hourMinuteFormatExplanation')"
                            class="text-sm text-primary-500 dark:text-primary-100"
                            v-text="totalBreakTime ?? 0"
                        />
                    </div>

                    <div
                        v-if="
                            authStore.companyId === report.company.id &&
                            report.settlementTemplateName
                        "
                        class="col-span-3 flex flex-col"
                    >
                        <div class="flex items-center">
                            <span
                                class="text-xs font-semibold text-primary-500 dark:text-primary-300"
                                v-text="
                                    `${t('settlementTemplate')} (${report.settlementTemplateName})`
                                "
                            />
                        </div>
                        <div class="mt-1 flex flex-wrap space-x-2">
                            <div
                                v-for="templateRate in report.settlementTemplateRates"
                                :key="templateRate.rate?.id"
                                :style="{ backgroundColor: templateRate.rate?.color ?? '#333' }"
                                class="mb-2 flex items-center rounded-lg px-2 py-1 text-xs text-white"
                            >
                                <span
                                    class="mr-2 font-bold"
                                    v-text="templateRate.rate?.name ?? t('noRate')"
                                />

                                <span
                                    v-tooltip="t('hourMinuteFormatExplanation')"
                                    v-text="differenceFromMinutes(templateRate.duration)"
                                />
                            </div>
                        </div>
                    </div>
                    <div v-if="hasShadowReports" class="col-span-3 flex flex-col">
                        <span
                            class="mt-2 text-xs font-semibold text-primary-500 dark:text-primary-300"
                            v-text="t('contractorReports')"
                        />
                        <RouterLink
                            v-for="shadowReport in report.shadowReports"
                            :key="shadowReport.id"
                            :to="{
                                name: 'dr.reports.show',
                                params: { reportId: shadowReport.id },
                            }"
                            class="inline-flex items-center gap-1 text-sm font-semibold text-primary-400 cursor-pointer hover:text-primary-500 dark:text-primary-100 dark:hover:text-primary-300"
                        >
                            <span
                                class="rounded-full h-3 w-3 flex-shrink-0"
                                :style="{
                                    backgroundColor: shadowReportsMap.get(shadowReport.id)?.color,
                                }"
                            />
                            {{ shadowReportsMap.get(shadowReport.id)?.title }}
                            <mdi:open-in-new />
                        </RouterLink>
                    </div>
                    <div
                        v-if="
                            authStore.companyId === report.haulierCompany?.id &&
                            report.originalReportId
                        "
                        class="col-span-3 flex flex-col"
                    >
                        <span
                            class="mt-2 text-xs font-semibold text-primary-500 dark:text-primary-300"
                            v-text="t('originalReport')"
                        />
                        <RouterLink
                            :to="{
                                name: 'dr.reports.show',
                                params: { reportId: report.originalReportId },
                            }"
                            class="inline-flex items-center gap-1 text-sm font-semibold text-primary-400 cursor-pointer hover:text-primary-500 dark:text-primary-100 dark:hover:text-primary-300"
                        >
                            {{ t('originalReport') }}
                            <mdi:open-in-new />
                        </RouterLink>
                    </div>

                    <div v-if="(startNote?.length ?? 0) > 1" class="col-span-3 flex flex-col">
                        <span
                            class="mt-2 text-xs font-semibold text-primary-500 dark:text-primary-300"
                            v-text="t('startNote')"
                        />
                        <span class="whitespace-pre-line text-xs" v-text="startNote" />
                    </div>

                    <div v-if="(endNote?.length ?? 0) > 1" class="col-span-3 flex flex-col">
                        <span
                            class="mt-2 text-xs font-semibold text-primary-500 dark:text-primary-300"
                            v-text="t('endNote')"
                        />
                        <span class="whitespace-pre-line text-xs" v-text="endNote" />
                    </div>

                    <img
                        class="qrcode col-span-3 ml-auto hidden print:block"
                        style="width: 180px; height: 180px"
                    />

                    <div v-if="report.tours.length > 0" class="col-span-3 flex flex-col">
                        <span
                            class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                            v-text="t('tours')"
                        />
                        <div v-for="tour in report.tours" :key="tour.id">
                            <RouterLink
                                :to="{
                                    name: 'dm.tours.show',
                                    params: { tourId: tour.id },
                                }"
                                class="text-xl font-semibold text-primary-500 dark:text-primary-100 hover:text-primary-400 dark:hover:text-primary-300 cursor-pointer"
                                v-text="tour.name"
                            />
                        </div>
                    </div>
                </div>
                <MapComponent
                    v-if="markers"
                    :markers="markers"
                    :tracking-path="reportTrackingEvents"
                    class="h-96 w-96 grow rounded-r-lg overflow-hidden"
                    :pan-to="mapLocation"
                    :connect-markers="reportTrackingEvents.length === 0"
                />
            </section>

            <MyPanel bordered>
                <MyTable
                    :loading="loading"
                    :rows="report.events"
                    disable-ordering
                    disable-actions
                    disable-search
                    disable-footer
                    disable-sorting
                    disable-column-preferences
                    table-id="dr-reports-events"
                    class="rounded-lg"
                >
                    <template
                        v-if="authStore.companyId && isNoReadonly && !report.originalReportId"
                        #actionRow="{ row }"
                    >
                        <ActionRowItem
                            v-if="!hasOngoingEvent"
                            class="print:hidden"
                            @click="updateEvent(row.id)"
                        >
                            <mdi:pencil />
                        </ActionRowItem>
                    </template>
                    <MyTableColumn :name="t('type')" property="type" />
                    <template #type="{ row }">
                        <div class="flex">
                            <div
                                v-if="hasShadowReports"
                                :style="{
                                    backgroundColor: shadowReportsMap.get(row.shadowReportId ?? '')
                                        ?.color,
                                }"
                                class="absolute left-0 top-0 w-1 h-full"
                            />
                            <span class="pl-2 w-full truncate" v-text="eventTypeText(row)" />
                        </div>
                    </template>
                    <MyTableColumn
                        :name="t('referenceNumber')"
                        :width="170"
                        property="referenceNumber"
                    />

                    <MyTableColumn :name="t('arrived')" :width="170" property="startedAt" />
                    <template #startedAt="{ row }">
                        {{ row.type !== EventType.ReportStart ? datetime(row.startedAt) : '' }}
                    </template>
                    <MyTableColumn :name="t('departed')" :width="170" property="endedAt" />
                    <template #endedAt="{ row }">
                        <span v-if="row.endedAt === null" v-text="t('enRoute')" />
                        <span v-else>
                            {{
                                row.type !== EventType.ReportEnd ? datetime(row.endedAt || '') : ''
                            }}
                        </span>
                    </template>
                    <MyTableColumn :name="t('duration')" property="duration" />
                    <template #duration="{ row }">
                        <div v-tooltip="t('hourMinuteFormatExplanation')">
                            <span
                                v-if="
                                    row.type === EventType.ReportEnd ||
                                    row.type === EventType.ReportStart
                                "
                                v-text="''"
                            />
                            {{ differenceBetweenDates(row.startedAt, row.endedAt) }}
                        </div>
                    </template>
                    <MyTableColumn :name="t('wait')" property="waitTime" />
                    <template #waitTime="{ row }">
                        <div v-tooltip="t('hourMinuteFormatExplanation')">
                            <span v-if="row.type !== EventType.Customer" v-text="''" />
                            <span v-else>{{ differenceFromMinutes(row.waitTime) }}</span>
                        </div>
                    </template>
                    <MyTableColumn :name="t('customer')" property="customerCompany.name" />
                    <MyTableColumn :name="t('address')" property="address" />
                    <MyTableColumn :name="t('km')" property="kilometerCount" />
                    <template #kilometerCount="{ row }">
                        <div class="flex">
                            <span class="w-full self-center truncate" v-text="row.kilometerCount" />
                            <MyButton
                                v-if="row.kilometerCountImage"
                                class="ml-2 hover:bg-dark-300"
                                icon
                                size="micro"
                                style="width: 20px"
                                @click="showImage(row.kilometerCountImage)"
                            >
                                <mdi:eye />
                            </MyButton>
                        </div>
                    </template>

                    <MyTableColumn :name="t('kmSum')" property="kilometerSum" />
                    <template #kilometerSum="{ row }">
                        {{ row.kilometerCount - report.events[0].kilometerCount }}
                    </template>
                    <MyTableColumn :name="t('registrationNumberShort')" property="units" />
                    <template #units="{ row }">{{ getEventRegistrationNumbers(row) }}</template>

                    <MyTableColumn :name="t('arrivalNote')" property="arrivalNote" />

                    <template #arrivalNote="{ row }">
                        <div class="flex">
                            <span class="w-full self-center truncate" v-text="row.arrivalNote" />
                            <MyButton
                                v-if="row.images.find((i) => i.type === EventImageType.Arrival)"
                                class="ml-2 hover:bg-dark-300"
                                icon
                                size="micro"
                                style="width: 20px"
                                @click="showArrivalImages(row)"
                            >
                                <mdi:eye />
                            </MyButton>
                        </div>
                    </template>

                    <MyTableColumn :name="t('departureNote')" property="departureNote" />

                    <MyTableColumn :name="t('loaded')" property="shipmentLoaded" boolean-column />
                    <template #departureNote="{ row }">
                        <div class="flex">
                            <span class="w-full self-center truncate" v-text="row.departureNote" />
                            <MyButton
                                v-if="row.images.find((i) => i.type === EventImageType.Departure)"
                                class="ml-2 hover:bg-dark-300"
                                icon
                                size="micro"
                                style="width: 20px"
                                @click="showDepartureImages(row)"
                            >
                                <mdi:eye />
                            </MyButton>
                        </div>
                    </template>

                    <MyTableColumn
                        :name="t('unloaded')"
                        property="shipmentUnloaded"
                        boolean-column
                    />
                    <MyTableColumn class="print:hidden" :name="t('geo')" property="geo" />

                    <template #geo="{ row }">
                        <a
                            v-if="row.location"
                            v-tooltip="row.address"
                            :href="googleMapsLink(row.location.latitude, row.location.longitude)"
                            target="_blank"
                            class="hover:bg-dark-300"
                            icon
                            size="micro"
                            @mouseover="panToLocation(row.location)"
                        >
                            <mdi:map-marker />
                        </a>
                    </template>
                    <MyTableColumn
                        :hidden="!eventActionsExist"
                        class="print:hidden"
                        :name="''"
                        property="actions"
                    />

                    <MyTableColumn
                        v-if="hasShadowReports"
                        :name="t('contractor')"
                        property="contractor"
                    />
                    <template #contractor="{ row }">
                        {{ shadowReportsMap.get(row.shadowReportId ?? '')?.report.company.name }}
                    </template>

                    <template #actions="{ row }">
                        <div class="flex items-center space-x-2">
                            <div v-for="action in row.actions" :key="action.actionableId">
                                <RouterLink
                                    v-if="action.type === EventActionType.PackagingTransaction"
                                    v-tooltip="t('packagingTransaction')"
                                    :to="{
                                        name: 'packaging.transactions.show',
                                        params: { transactionId: action.actionableId },
                                    }"
                                >
                                    <mdi:package-variant-closed />
                                </RouterLink>

                                <RouterLink
                                    v-if="action.type === EventActionType.PODReceipt"
                                    v-tooltip="t('podReceipt')"
                                    :to="{
                                        name: 'pod.receipts.show',
                                        params: { receiptId: action.actionableId },
                                    }"
                                >
                                    <mdi:receipt />
                                </RouterLink>

                                <RouterLink
                                    v-if="
                                        action.type === EventActionType.AttachUnit &&
                                        action.damageReportId
                                    "
                                    v-tooltip="`${t('pickup')} ${t('damageReport')}`"
                                    :to="{
                                        name: 'dmr.reports.show',
                                        params: { reportId: action.damageReportId },
                                    }"
                                >
                                    <mdi:truck-trailer />
                                </RouterLink>

                                <RouterLink
                                    v-if="
                                        action.type === EventActionType.DetachUnit &&
                                        action.damageReportId
                                    "
                                    v-tooltip="`${t('dropOff')} ${t('damageReport')}`"
                                    :to="{
                                        name: 'dmr.reports.show',
                                        params: { reportId: action.damageReportId },
                                    }"
                                >
                                    <mdi:truck-trailer />
                                </RouterLink>
                            </div>

                            <RouterLink
                                v-for="tourEvent in row.tourEvents"
                                :key="tourEvent.id"
                                v-tooltip="t('showShipment')"
                                class="print:hidden"
                                :to="{
                                    name: 'dm.shipments.show',
                                    params: { shipmentId: tourEvent.shipmentId },
                                }"
                            >
                                <mdi:eye />
                            </RouterLink>

                            <RouterLink
                                v-if="row.tourId"
                                v-tooltip="t('showTour')"
                                class="print:hidden"
                                :to="{
                                    name: 'dm.tours.show',
                                    params: { tourId: row.tourId },
                                }"
                            >
                                <mdi:eye />
                            </RouterLink>
                        </div>
                    </template>
                </MyTable>
            </MyPanel>
            <img
                :src="localAsset('assets/begreenprintlabel.png')"
                alt="begreen"
                class="absolute bottom-0 right-0 w-32 h-32 hidden opacity-30 print:block"
            />
        </div>

        <h1
            v-if="!report && !loading"
            class="my-auto text-center text-4xl font-bold"
            v-text="t('driverReportNotFound')"
        />
    </MyModal>

    <ShareReportModal v-model="shareReportOpen" :report-id="report?.id" />

    <ManageEventModal
        v-model="createEventModalOpen"
        :report-ended="report?.endedAt !== null"
        @close="createEventModalClosed"
    />

    <ManageEventModal
        v-model="updateEventModalOpen"
        :report-event="selectedEvent"
        :report-ended="report?.endedAt !== null"
        @close="updateEventModalClosed"
    />
    <StopReportModal
        v-if="report && stopReportModalOpen && !report.originalReportId"
        v-model="stopReportModalOpen"
        :report-id="report.id"
        @close="stopReportModalClosed"
    />

    <UpdateReportTypeModal
        v-if="report && !report.originalReportId"
        v-model="updateReportTypeModalOpen"
        :report="report"
        @close="updateReportTypeModalOpen = false"
    />
</template>
